'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { Gender } from '@workspace/database';

import { getPosesForGenerator } from '~/actions/poses/get-poses-for-generator';
import {
  PoseCard,
  SelectedPoseCard
} from '~/components/generator/cards/pose-cards';
import {
  ItemSelectionStep,
  type ItemSelectionStepProps
} from '~/components/generator/shared/item-selection-step';
import type { ItemSortOption } from '~/components/generator/shared/item-selection-step';
import { genderLabel } from '~/lib/labels';
import type { CompleteGeneratorSchema } from '~/schemas/generator/complete-generator-schema';
import { GetPosesSortBy } from '~/schemas/poses/get-poses-schema';
import type { PoseDto } from '~/types/dtos/pose-dto';

const poseSortOptions: ItemSortOption[] = [
  { value: GetPosesSortBy.Title, label: 'Title' },
  { value: GetPosesSortBy.Gender, label: 'Gender' },
  { value: GetPosesSortBy.CreatedAt, label: 'Created Date' },
  { value: GetPosesSortBy.UpdatedAt, label: 'Updated Date' }
];

type GeneratorPosesStepProps = React.HtmlHTMLAttributes<HTMLDivElement> &
  ItemSelectionStepProps<PoseDto, { labels: string[] }>;

export function GeneratorPosesStep({
  ...props
}: Omit<
  GeneratorPosesStepProps,
  | 'fetcher'
  | 'onSelectionChange'
  | 'selectedIds'
  | 'ItemCardComponent'
  | 'SelectedItemCardComponent'
  | 'getFilterDefs'
  | 'title'
  | 'description'
  | 'maxSelection'
  | 'sortOptions'
>): React.JSX.Element {
  const methods = useFormContext<CompleteGeneratorSchema>();
  const selectedPoses = methods.watch('poseStep.poses') || [];
  const [fetchedPoses, setFetchedPoses] = React.useState<PoseDto[]>([]);
  const [genderGroup, setGenderGroup] = React.useState<Gender>(Gender.FEMALE);

  // Update selected poses when fetched poses change
  React.useEffect(() => {
    const currentSelectedPoses = methods.getValues('poseStep.poses') || [];
    if (currentSelectedPoses.length > 0) {
      const updatedPoses = currentSelectedPoses.map((selectedPose) => {
        // If this is a fallback pose (title is "Loading..."), try to find the real data
        if (selectedPose.title === 'Loading...') {
          const realPoseData = fetchedPoses.find(
            (p) => p.id === selectedPose.id
          );
          if (realPoseData) {
            return realPoseData;
          }
        }
        return selectedPose;
      });

      // Only update if there are changes
      const hasChanges = updatedPoses.some(
        (pose, index) =>
          pose.id !== currentSelectedPoses[index]?.id ||
          pose.title !== currentSelectedPoses[index]?.title
      );

      if (hasChanges) {
        methods.setValue('poseStep.poses', updatedPoses, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }
  }, [fetchedPoses, methods]);

  const fetcher = React.useCallback(
    async (args: any) => {
      const result = await getPosesForGenerator({
        pageIndex: args.pageIndex,
        pageSize: args.pageSize,
        sortBy: args.sortBy,
        sortDirection: args.sortDirection,
        searchQuery: args.searchQuery,
        // Group filter controls gender; override any inline filter selections
        genders: [genderGroup],
        labels: args.filters.labels ?? []
      });

      if (result?.data && !result.serverError && !result.validationErrors) {
        const { poses, filteredCount, totalCount } = result.data;
        // Store fetched poses for building complete pose objects
        setFetchedPoses((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newPoses = poses.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newPoses];
        });
        return {
          items: poses,
          filteredCount,
          totalCount,
          labels: poses.flatMap((pose) => pose.labels)
        };
      }

      return { items: [], filteredCount: 0, totalCount: 0, labels: [] };
    },
    [genderGroup]
  );

  const onSelectionChange = React.useCallback(
    (selectedIds: string[]) => {
      const newPoses = selectedIds.map((id) => {
        // Find the pose data from fetched poses
        const poseData = fetchedPoses.find((p) => p.id === id);
        if (poseData) {
          // Store the complete pose data
          return poseData;
        }

        // Fallback: create a minimal pose object if data not found
        // This prevents the error and allows the selection to work
        // The complete data will be fetched when needed
        console.warn(
          `Pose data not immediately available for id: ${id}, using fallback`
        );
        return {
          id,
          title: 'Loading...',
          description: '',
          gender: Gender.FEMALE,
          labels: [],
          createdAt: new Date(),
          updatedAt: new Date()
        } as PoseDto;
      });
      methods.setValue('poseStep.poses', newPoses, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    },
    [methods, fetchedPoses]
  );

  const getFilterDefs = React.useCallback(
    (fetcherResult: { labels: string[] } | undefined) => [
      {
        name: 'labels',
        title: 'Labels',
        options:
          fetcherResult?.labels
            .filter((label, index, self) => self.indexOf(label) === index)
            .map((label: string) => ({
              value: label,
              label: label
            })) ?? []
      }
    ],
    []
  );

  return (
    <ItemSelectionStep
      fetcher={fetcher}
      onSelectionChange={onSelectionChange}
      selectedIds={selectedPoses?.map((p) => p.id) || []}
      ItemCardComponent={PoseCard}
      SelectedItemCardComponent={SelectedPoseCard}
      getFilterDefs={getFilterDefs}
      sortOptions={poseSortOptions}
      groupFilter={{
        options: [
          { value: Gender.FEMALE, label: genderLabel[Gender.FEMALE] },
          { value: Gender.MALE, label: genderLabel[Gender.MALE] }
        ],
        value: genderGroup,
        onValueChange: (value: string) => setGenderGroup(value as Gender),
        srLabel: 'Gender'
      }}
      title="Select your pose"
      description="Select a pose for your model."
      searchPlaceholder="Search by title or description..."
      maxSelection={4}
      pageSize={100}
      {...props}
    />
  );
}
